// PA-3b — TOC platform-staff web API wrapper. Staff-only, read-only.
// `GET /api/platform/restaurants` is the source for the "view any restaurant" picker.
import type { ApiClient } from './client'

export interface PlatformRestaurant {
  id: string
  name: string
  clerkOrgId: string
  createdAt: string
  // DEF-80c — present on the list; platform admins also see PURGED (and CANCELED) tenants, marked in the picker.
  lifecycleStatus?: string
}

export const fetchPlatformRestaurants = (api: ApiClient) =>
  api.get<{ data: PlatformRestaurant[] }>('/platform/restaurants').then(r => r.data)

// m0-2b — explicit self-serve provisioning. The ONE way a tenant is created: an authed user whose
// active Clerk org has no Restaurant yet POSTs here (idempotent; 201 create / 200 already-exists).
// Called by the bootstrap when GET /api/me returns needsProvisioning.
export const provisionTenant = (api: ApiClient) =>
  api.post<{ data: { restaurantId: string; created: boolean } }>('/platform/provision', {}).then(r => r.data)

// DEF-207b-2 — the owner's own downloads (owner only on the server; open in every lifecycle state). Both come back as
// a file: the full data bundle (JSON, m0-4b) and every stored file as a zip (DEF-207b-1).
export const downloadOwnerData = (api: ApiClient) => api.getBlob('/platform/export')
export const downloadOwnerFiles = (api: ApiClient) => api.getBlob('/platform/export/files')

// ── m0-5c-1 — platform-admin console (cross-tenant) ──────────────────────────────────────────────
// These read the m0-5a endpoints under /api/platform. The server gate is requirePlatformAdmin
// (Staff-org member AND a platform_admins grant); a staff member without the grant gets 403.
export type TenantLifecycleStatus =
  | 'PROVISIONING' | 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'CANCELED' | 'PURGED'

export interface AdminTenant {
  id: string
  name: string
  clerkOrgId: string
  lifecycleStatus: TenantLifecycleStatus
  trialEndsAt: string | null
  canceledAt: string | null
  purgeAfter: string | null
  purgedAt: string | null
  createdAt: string
  activatedAt: string | null
}

export interface TenantLifecycleEvent {
  id: string
  restaurantId: string
  at: string
  actorUserId: string | null
  actorKind: string
  fromStatus: string | null
  toStatus: string
  reason: string | null
}

// GET /api/platform/tenants — every tenant + lifecycle status + activation (newest first).
export const fetchAdminTenants = (api: ApiClient) =>
  api.get<{ data: AdminTenant[] }>('/platform/tenants').then(r => r.data)

// GET /api/platform/tenants/:id/events — the tenant's lifecycle audit trail (newest first).
export const fetchTenantEvents = (api: ApiClient, id: string) =>
  api.get<{ data: TenantLifecycleEvent[] }>(`/platform/tenants/${id}/events`).then(r => r.data)

// GET /api/platform/tenants/:id/export — the full tenant data bundle (returned as JSON `data`).
export const fetchTenantExport = (api: ApiClient, id: string) =>
  api.get<{ data: unknown }>(`/platform/tenants/${id}/export`).then(r => r.data)

// ── m0-5c-2 — platform-admin lifecycle WRITES (reversible; over the live m0-5b-1 POSTs) ────────────
// Each POSTs to /api/platform/tenants/:id/{suspend,reactivate,cancel}. The server gate is
// requirePlatformAdmin (Staff-org member AND a platform_admins grant). An optional `reason` is recorded
// in the platform audit trail; a blank one lets the server record its default label. The API returns
// 404 if the tenant is gone and 409 if the transition is invalid or the status changed concurrently.
export interface TenantLifecycleWriteResult {
  id: string
  lifecycleStatus: TenantLifecycleStatus
  purgeAfter?: string | null
}

// Only send a body key when the admin actually typed a reason (trimmed); otherwise the server default wins.
const reasonBody = (reason?: string | null) => {
  const trimmed = reason?.trim()
  return trimmed ? { reason: trimmed } : {}
}

// POST /api/platform/tenants/:id/suspend — TRIAL/ACTIVE → SUSPENDED (access frozen to reactivate/export).
export const suspendTenant = (api: ApiClient, id: string, reason?: string | null) =>
  api.post<{ data: TenantLifecycleWriteResult }>(`/platform/tenants/${id}/suspend`, reasonBody(reason)).then(r => r.data)

// POST /api/platform/tenants/:id/reactivate — SUSPENDED|CANCELED → TRIAL (access restored).
export const reactivateTenant = (api: ApiClient, id: string, reason?: string | null) =>
  api.post<{ data: TenantLifecycleWriteResult }>(`/platform/tenants/${id}/reactivate`, reasonBody(reason)).then(r => r.data)

// POST /api/platform/tenants/:id/cancel — TRIAL/ACTIVE/SUSPENDED → CANCELED (+90-day retention clock).
export const cancelTenant = (api: ApiClient, id: string, reason?: string | null) =>
  api.post<{ data: TenantLifecycleWriteResult }>(`/platform/tenants/${id}/cancel`, reasonBody(reason)).then(r => r.data)

// ── m0-5c-3 — early-purge (IRREVERSIBLE) + admin roster (grant / revoke / list) ────────────────────
// Pure clients of the requirePlatformAdmin-gated m0-5b-2 / m0-5b-3 routes.
export interface TenantPurgeResult {
  id: string
  lifecycleStatus: TenantLifecycleStatus
  totalRows: number
}

// POST /api/platform/tenants/:id/purge — early-purge an ALREADY-CANCELED tenant NOW (skip the 90-day wait).
// Both fields required; the server 422s on confirm_name_required / reason_required / confirm_name_mismatch
// and 409s on a non-CANCELED tenant or a concurrent status change. Irreversible.
export const purgeTenant = (api: ApiClient, id: string, confirmName: string, reason: string) =>
  api.post<{ data: TenantPurgeResult }>(`/platform/tenants/${id}/purge`, { confirmName, reason }).then(r => r.data)

export interface PlatformAdminRow {
  clerkUserId: string
  grantedByClerkUserId: string | null
  grantedAt: string
  note: string | null
}

// GET /api/platform/admins — the current platform.admin holders.
export const fetchPlatformAdmins = (api: ApiClient) =>
  api.get<{ data: PlatformAdminRow[] }>('/platform/admins').then(r => r.data)

// POST /api/platform/admins/:clerkUserId/grant — idempotent grant (check-time model); optional note.
export const grantPlatformAdmin = (api: ApiClient, clerkUserId: string, note?: string | null) => {
  const trimmed = note?.trim()
  return api
    .post<{ data: { clerkUserId: string; created: boolean } }>(
      `/platform/admins/${encodeURIComponent(clerkUserId)}/grant`,
      trimmed ? { note: trimmed } : {},
    )
    .then(r => r.data)
}

// POST /api/platform/admins/:clerkUserId/revoke — lockout-safe (404 not-an-admin; 409 bootstrap / last-admin).
export const revokePlatformAdmin = (api: ApiClient, clerkUserId: string) =>
  api
    .post<{ data: { clerkUserId: string } }>(`/platform/admins/${encodeURIComponent(clerkUserId)}/revoke`, {})
    .then(r => r.data)

// ── DEF-80b — purge archives (platform.admin only) ─────────────────────────────────────────────────
// Every purge saves the tenant's full export first; these list the archives (metadata only) and download
// one (the export bundle). Both are requirePlatformAdmin-gated and audited server-side.
export interface PurgeArchiveSummary {
  id: string
  targetRestaurantId: string
  restaurantName: string
  reason: string
  archivedAt: string
  expiresAt: string
  legalHold: boolean
  legalHoldChangedAt: string | null
  legalHoldChangedBy: string | null
  legalHoldReason: string | null
  tableCount: number
  rowCount: number
  payloadBytes: number
}

export const fetchPurgeArchives = (api: ApiClient) =>
  api.get<{ data: PurgeArchiveSummary[] }>('/platform/archives').then(r => r.data)

export const fetchPurgeArchive = (api: ApiClient, id: string) =>
  api.get<{ data: unknown }>(`/platform/archives/${id}`).then(r => r.data)

// DEF-80 — place (hold=true) or release (hold=false) an archive's legal hold. A reason is required.
export const setArchiveLegalHold = (api: ApiClient, id: string, hold: boolean, reason: string) =>
  api.post<{ data: PurgeArchiveSummary; changed: boolean }>(`/platform/archives/${id}/legal-hold`, { hold, reason }).then(r => r.data)

